import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';
import { resetLanguage, changeLanguage, getUserLangKey } from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // SECURITY: No token state needed - httpOnly cookie handles authentication automatically
  const [activeRole, setActiveRole] = useState(localStorage.getItem('activeRole') || null);

  useEffect(() => {
    // Don't auto-check auth on login pages to prevent redirect loops
    const currentPath = window.location.pathname;
    const isLoginPage = currentPath === '/signin' ||
                       currentPath === '/super-admin' ||
                       currentPath === '/super-admin/' ||
                       currentPath === '/super-admin/login';

    // Public onboarding/signup pages — skip auth check entirely
    // These pages use registration tokens, not auth cookies
    const isPublicOnboarding = currentPath.startsWith('/onboarding/') ||
                               currentPath.startsWith('/signup');

    if (isPublicOnboarding) {
      // No auth check needed — just mark loading as done
      setLoading(false);
    } else if (!isLoginPage) {
      checkAuthStatus();
    } else {
      // On login pages: only call /me if there is a stored user session in
      // localStorage — meaning the user has logged in before and may still
      // have a valid httpOnly cookie.  This auto-redirects already-logged-in
      // users who navigate back to /signin while avoiding a guaranteed 401
      // for genuinely unauthenticated visitors (which shows as console noise
      // and wastes a round-trip).
      const storedUser = localStorage.getItem('currentUser');
      const hasStoredSession = storedUser && storedUser !== 'null' && storedUser !== 'undefined';
      if (hasStoredSession) {
        checkAuthStatus();
      } else {
        setLoading(false);
      }
    }
  }, []);

  // Apply the user's stored language preference from localStorage immediately
  // after we know who they are.  This gives instant feedback (no flash of
  // English) while SettingsContext fetches the authoritative value from the
  // backend in the background.
  const applyStoredLanguage = (userData) => {
    const userId = userData?.user_id || userData?.email || null;
    const key = getUserLangKey(userId);
    const storedLang = key && localStorage.getItem(key);
    if (storedLang) {
      changeLanguage(storedLang);
    }
  };

  const checkAuthStatus = async () => {
    try {
      // SECURITY: httpOnly cookie sent automatically with request
      // No need to check localStorage - backend validates cookie
      const userData = await authAPI.getCurrentUser();
      setCurrentUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      // Restore language preference immediately from localStorage so the UI
      // doesn't flash English while SettingsContext makes its API call.
      applyStoredLanguage(userData);
      setIsAuthenticated(true);
    } catch (error) {
      // User not authenticated (no valid cookie or cookie expired)
      if (error.response?.status === 401) {
        console.log('Not authenticated');
      } else {
        console.error('Auth check failed:', error);
      }
      // Clear stale user data
      localStorage.removeItem('currentUser');
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Timestamp of the last silent user-data refresh (epoch ms).
  // Used by refreshUser() to enforce a cooldown so rapid page switches
  // don't spam the /me endpoint.
  const lastRefreshAt = useRef(0);

  /**
   * Silent permission refresh — called on every sidebar page switch.
   * Re-fetches /me and updates currentUser (and therefore all permission
   * hooks) without touching the `loading` flag, so no spinner is shown.
   * Enforces a 10-second cooldown to prevent spam on rapid navigation.
   */
  const refreshUser = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshAt.current < 10_000) return; // 10 s cooldown
    lastRefreshAt.current = now;
    try {
      const userData = await authAPI.getCurrentUser();
      setCurrentUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      applyStoredLanguage(userData);
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('currentUser');
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
      // Non-401 errors (network blip, 5xx) are silently ignored —
      // the user keeps their current permissions until the next refresh.
    }
  }, []);

  const login = async (email, password, mfaCode = null) => {
    try {
      const response = await authAPI.login(email, password, mfaCode);

      // Handle MFA challenge (backend returns 200 with mfa_required: true)
      if (response.mfa_required) {
        return {
          success: false,
          requiresMFA: true,
          mfaToken: response.access_token,
          mfaMethods: response.mfa_methods || [],
          userId: response.user_id,
          message: response.message || 'MFA verification required'
        };
      }

      // SECURITY: httpOnly cookie set automatically by backend for HTTP requests
      // However, WebSocket connections cannot use httpOnly cookies (protocol limitation)
      // Store access_token in localStorage ONLY for WebSocket authentication
      if (response.access_token) {
        localStorage.setItem('wsAuthToken', response.access_token);
      }

      // Immediately fetch /me to populate user correctly
      const userData = await authAPI.getCurrentUser();
      localStorage.setItem('currentUser', JSON.stringify(userData));
      // Restore language preference immediately from localStorage so the UI
      // doesn't flash English while SettingsContext makes its API call.
      applyStoredLanguage(userData);
      setCurrentUser(userData);
      setIsAuthenticated(true);

      // Check if password reset is required (first login with temp password)
      const passwordResetRequired = userData?.password_reset_required || response?.user?.password_reset_required;

      return {
        success: true,
        user: userData,
        requiresMFA: false,
        passwordResetRequired: passwordResetRequired
      };
    } catch (error) {
      console.error('Login failed:', error);
      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.detail || 'Login failed';
        if (errorMessage.includes('MFA code required')) {
          return { success: false, requiresMFA: true, message: errorMessage };
        }
        return { success: false, requiresMFA: false, message: errorMessage };
      }
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'object' && detail?.password_expired) {
          return { success: false, passwordExpired: true, message: detail.message || 'Your password has expired.' };
        }
        return { success: false, requiresMFA: false, message: typeof detail === 'string' ? detail : 'Access denied' };
      }
      return { success: false, requiresMFA: false, message: 'Login failed. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      // SECURITY: Backend clears httpOnly cookie on logout
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
      setIsAuthenticated(false);
      // Clear user data from localStorage
      localStorage.removeItem('currentUser');
      // Clear WebSocket auth token
      localStorage.removeItem('wsAuthToken');
      // Reset i18n to English so the next user who logs in on this browser
      // sees the default language until their own preference is loaded.
      // resetLanguage() also cleans up any legacy unscoped keys.
      resetLanguage();
    }
  };

  const setupMFA = async () => {
    try {
      const response = await authAPI.setupMFA();
      return { success: true, data: response };
    } catch (error) {
      console.error('MFA setup failed:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'MFA setup failed' 
      };
    }
  };

  const verifyMFA = async (code) => {
    try {
      await authAPI.verifyMFA(code);
      
      // Refresh user data to get updated MFA status
      const userData = await authAPI.getCurrentUser();
      setCurrentUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      console.error('MFA verification failed:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'MFA verification failed' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Password change failed:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Password change failed' 
      };
    }
  };

  const hasPermission = (permission) => {
    if (!currentUser) return false;

    // Check base permissions
    if (currentUser.permissions?.includes(permission)) {
      return true;
    }

    // Check custom role permissions (e.g., approve_leaves, view_screenshots, etc.)
    // NOTE: System-role users now also receive custom_role_permissions (containing
    // module_access from their template), but the implied-permissions mapping below
    // is only meaningful for custom-role users. System roles get their permissions
    // from ROLE_PERMISSIONS on the backend (already in currentUser.permissions).
    const customRolePermissions = currentUser.custom_role_permissions;
    if (customRolePermissions && currentUser.custom_role_id) {
      // Check additional_permissions array
      if (customRolePermissions.additional_permissions?.includes(permission)) {
        return true;
      }

      // Check module_access - users with any module access implicitly have certain permissions
      const moduleAccess = customRolePermissions.module_access || [];
      if (moduleAccess.length > 0) {
        // Map module access to implied permissions
        const modulePermissionMap = {
          'overview': ['view_employees', 'view_analytics'],
          'devices': ['view_employees', 'manage_devices'],
          'screenshots': ['view_screenshots', 'view_employees'],
          'productivity': ['view_productivity', 'view_analytics', 'view_employees'],
          'reports': ['view_reports', 'view_analytics', 'view_employees'],
          'attendance-management': ['view_employees', 'approve_leaves'],
          'alerts': ['view_employees'],
          'settings': ['view_employees'],
        };

        for (const module of moduleAccess) {
          if (module.permission && module.permission !== 'none') {
            const impliedPermissions = modulePermissionMap[module.module_id] || [];
            if (impliedPermissions.includes(permission)) {
              return true;
            }
          }
        }
      }
    }

    return false;
  };

  const hasRole = (role) => {
    if (!currentUser) return false;

    // Direct role match
    if (currentUser.role === role) {
      return true;
    }

    // Users with custom roles and dashboard access should be treated as having elevated access
    // For admin-level routes, check if user has custom role with dashboard access
    const adminRoles = ['admin', 'org_admin', 'client_admin', 'super_client_admin'];
    if (adminRoles.includes(role)) {
      // If user has custom role with module access, they should be treated as having admin-level access
      const customRoleModuleAccess = currentUser.custom_role_permissions?.module_access || [];
      if (currentUser.custom_role_id && customRoleModuleAccess.length > 0) {
        return true;
      }
      // Also check has_dashboard_access flag
      if (currentUser.has_dashboard_access) {
        return true;
      }
    }

    return false;
  };

  // Get available roles for the current user
  const getAvailableRoles = () => {
    if (!currentUser) return [];

    const roles = [];
    const userRole = currentUser.role || '';
    const adminRoles = ['super_admin', 'admin', 'org_admin', 'client_admin', 'super_client_admin'];

    // Check if user has admin dashboard access.
    // 'manager' role users can have admin dashboard enabled per-user — check
    // has_dashboard_access (set by /me endpoint) and admin_dashboard_enabled
    // (stored directly on the user doc for system-role managers).
    const hasAdminAccess = adminRoles.includes(userRole) ||
                           currentUser.custom_role_id ||
                           currentUser.has_dashboard_access ||
                           currentUser.admin_dashboard_enabled;

    if (hasAdminAccess) {
      roles.push({
        id: 'admin',
        label: 'Admin Dashboard',
        icon: 'Monitor',
        path: '/dashboard',
        description: 'Manage organization settings and view analytics'
      });
    }

    // Employee Portal: all users (admin, manager, employee) are also employees and
    // should have access to the Employee Portal by default.
    // - super_admin is excluded (platform-level accounts, not org employees).
    // - Any user where employee_portal_access has been explicitly set to false by an
    //   admin (via the Role Management toggle) is excluded.
    // - All other users — including client_admin and manager — get it by default.
    const isSuperAdmin = userRole === 'super_admin';
    if (!isSuperAdmin && currentUser.employee_portal_access !== false) {
      roles.push({
        id: 'employee',
        label: 'Employee Portal',
        icon: 'User',
        path: '/employee-portal',
        description: 'View your personal productivity and consents'
      });
    }

    return roles;
  };

  // Switch active role and navigate
  const switchRole = (roleId) => {
    setActiveRole(roleId);
    localStorage.setItem('activeRole', roleId);
  };

  // Check if user has multiple roles
  const hasMultipleRoles = () => {
    return getAvailableRoles().length > 1;
  };

  // ── Impersonation helpers ──────────────────────────────────────────────────

  /**
   * Exchange the impersonation JTI for a real session cookie as the target user.
   * Updates currentUser to reflect the impersonated identity.
   * Returns the target user info from the backend.
   */
  const startImpersonation = async (jti) => {
    const result = await authAPI.impersonationExchange(jti);
    // Refresh the auth context — /me will now return the target user
    await checkAuthStatus();
    return result;
  };

  /**
   * Exit impersonation, restore the SA's cookie, and update auth context.
   * The caller should navigate to /super-admin/dashboard after this resolves.
   */
  const exitImpersonation = async () => {
    const result = await authAPI.impersonationExit();
    await checkAuthStatus();
    return result;
  };

  /**
   * Emergency exit when the impersonation session has ALREADY EXPIRED.
   * The auth_token cookie is gone; this uses the sa_actor_id cookie (kept
   * alive 5 min beyond the session TTL) to restore the SA's session and
   * write the SESSION_ENDED audit event.
   * The caller must hard-redirect to /super-admin/dashboard after this resolves.
   */
  const timeoutExitImpersonation = async () => {
    const result = await authAPI.impersonationTimeoutExit();
    // checkAuthStatus will now read the fresh SA cookie set by the endpoint
    await checkAuthStatus();
    return result;
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    // SECURITY: No token exposed - httpOnly cookie handles authentication
    activeRole,
    login,
    logout,
    setupMFA,
    verifyMFA,
    changePassword,
    hasPermission,
    hasRole,
    checkAuthStatus,
    refreshUser,
    getAvailableRoles,
    switchRole,
    hasMultipleRoles,
    setCurrentUser,
    setIsAuthenticated,
    // Impersonation
    startImpersonation,
    exitImpersonation,
    timeoutExitImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;